import { FormEvent, useRef, useState } from 'react';
import { AdaptiveSheet } from './AdaptiveSheet';
import { UiIcon } from './ActionIcon';
import type { AppRoute } from '../routes';
import { dishApi } from '../features/dishes/api';
import { menuApi } from '../features/menus/api';
import { recipeApi } from '../features/recipes/api';
import { useI18n } from '../i18n';
import { completeOverlayAction } from '../shared/overlay-history';

type CreateKind = 'dish' | 'recipe' | 'menu';

export function QuickCreateMenu({
  canCreateKitchenItem,
  canImport,
  canCreateMenu,
  navigate,
  placement = 'mobile'
}: {
  canCreateKitchenItem: boolean;
  canImport: boolean;
  canCreateMenu: boolean;
  navigate: (route: AppRoute) => void;
  placement?: 'mobile' | 'desktop';
}) {
  const { locale } = useI18n();
  const zh = locale === 'zh-CN';
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<CreateKind | null>(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const completedCreateRef = useRef(false);

  function close() {
    if (busy && !completedCreateRef.current) return;
    completedCreateRef.current = false;
    setOpen(false);
    setKind(null);
    setName('');
    setError('');
  }

  function finishCreate(route: AppRoute) {
    completedCreateRef.current = true;
    setBusy(false);
    if (completeOverlayAction(() => navigate(route))) return;
    close();
    navigate(route);
  }

  function choose(next: CreateKind) {
    setKind(next);
    setName('');
    setError('');
  }

  async function create(event: FormEvent) {
    event.preventDefault();
    if (!kind || busy || !name.trim()) return;
    setBusy(true);
    setError('');
    try {
      const title = name.trim();
      if (kind === 'recipe') {
        const result = await recipeApi.create(title);
        finishCreate({ screen: 'recipes', entityId: result.id });
        return;
      }
      if (kind === 'dish') {
        const result = await dishApi.create(title);
        finishCreate({ screen: 'dishes', entityId: result.id });
        return;
      }
      const result = await menuApi.create(title);
      finishCreate({ screen: 'menus', entityId: result.id });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : (zh ? '新建失败' : 'Could not create'));
    } finally {
      setBusy(false);
    }
  }

  const available = canCreateKitchenItem || canImport || canCreateMenu;
  if (!available) return null;

  const sheet = <AdaptiveSheet
    open={open}
    eyebrow={zh ? '快速开始' : 'Quick start'}
    title={kind ? (zh ? '起个名字' : 'Give it a name') : (zh ? '你要做什么？' : 'What do you want to do?')}
    closeLabel={zh ? '关闭快速新建' : 'Close quick create'}
    onDismiss={close}
  >
    {!kind ? <div className="quick-create-options">
      {canCreateKitchenItem && <button type="button" onClick={() => choose('dish')}><strong>{zh ? '新建菜品' : 'New dish'}</strong><span>{zh ? '最终上桌的完整菜品' : 'A complete plated dish'}</span></button>}
      {canCreateKitchenItem && <button type="button" onClick={() => choose('recipe')}><strong>{zh ? '新建基础配方' : 'New base recipe'}</strong><span>{zh ? '酱汁、汤底、馅料等制作基础' : 'Sauces, stocks, fillings and preparations'}</span></button>}
      {canImport && <button type="button" onClick={() => navigate({ screen: 'imports' })}><strong>{zh ? '导入配方' : 'Import recipe'}</strong><span>{zh ? 'Excel、Word、PDF、图片或文字' : 'Excel, Word, PDF, images or text'}</span></button>}
      {canCreateMenu && <button type="button" onClick={() => choose('menu')}><strong>{zh ? '新建菜单' : 'New menu'}</strong><span>{zh ? '开始排这一季真正要上的菜' : 'Start arranging what you plan to serve'}</span></button>}
    </div> : <form className="quick-create-form" onSubmit={create}>
      <button type="button" className="text-button quick-create-back" disabled={busy} onClick={() => { setKind(null); setName(''); setError(''); }}><UiIcon name="back" size={16} /><span>{zh ? '返回' : 'Back'}</span></button>
      <label>{kind === 'dish' ? (zh ? '菜品名称' : 'Dish name') : kind === 'recipe' ? (zh ? '基础配方名称' : 'Base recipe name') : (zh ? '菜单名称' : 'Menu name')}
        <input autoFocus value={name} onChange={event => setName(event.target.value)} placeholder={kind === 'dish' ? (zh ? '例如：炭烤黄牛' : 'e.g. Charcoal-grilled beef') : kind === 'recipe' ? (zh ? '例如：二荆条黄油' : 'e.g. Erjingtiao butter') : (zh ? '例如：2026 秋季菜单' : 'e.g. Autumn 2026')} required />
      </label>
      {error && <p className="form-error">{error}</p>}
      <button className="primary-button" disabled={busy || !name.trim()}>{busy ? (zh ? '正在建立…' : 'Creating…') : (zh ? '开始编辑' : 'Start editing')}</button>
    </form>}
  </AdaptiveSheet>;

  return <>
    <button type="button" className={`quick-create-trigger ${placement}`} aria-label={zh ? '快速新建' : 'Quick create'} aria-expanded={open} onClick={() => setOpen(true)}>
      <span className="quick-create-trigger-icon" aria-hidden="true"><UiIcon name="add" size={20} /></span>{placement === 'desktop' && <strong>{zh ? '新建 / 导入' : 'New / Import'}</strong>}
    </button>
    {sheet}
  </>;
}
